import { useRef, useState } from "react";
import { formatMoney } from "../api";
import { workingOf } from "../working";
import { Alert, Modal } from "../ui";
import { shrink, type Taken, turned, usePastedImages } from "./photos";

/**
 * The booth keypads.
 *
 * Big keys, hit without looking down, for the two numbers a till has to take by hand:
 * what a customer is paying for something that is not counted, such as raw singles out
 * of a binder, and how many of a thing when tapping plus ten times would be silly.
 */

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "00", "0", "back"] as const;

function Keypad({ onDigit, onBack }: { readonly onDigit: (digit: string) => void; readonly onBack: () => void }): React.ReactElement {
  return (
    <div className="grid grid-cols-3 gap-2">
      {KEYS.map((key) => (
        <button
          key={key}
          type="button"
          aria-label={key === "back" ? "Rub out the last number" : key}
          className="ti-btn ti-btn-light !m-0 !py-4 !text-[1.25rem] font-semibold"
          onClick={() => (key === "back" ? onBack() : onDigit(key))}
        >
          {key === "back" ? <i className="ri-delete-back-2-line" /> : key}
        </button>
      ))}
    </div>
  );
}

/**
 * What the customer is paying for something the system does not count: raw singles out of
 * a binder, and anything at a show that is not on the list at all.
 *
 * The amount is typed in cents right to left the way a till works. Beside it go how many
 * there were, a few words saying what they were, and a photograph of them, because at a
 * show the booth sells things nobody has entered yet and the picture is the only record
 * of what actually went across the table. The amount is the one thing that has to be
 * right, so it is the amount for the lot and the line is one line: three cards for a
 * hundred dollars is a hundred dollars, not three of thirty three thirty three.
 */
export function PosAmount({
  name,
  floor,
  showCost,
  onClose,
  onDone,
}: {
  readonly name: string;
  /** What the shop pays for one, and the sum behind it, when anybody has said. */
  readonly floor: { cents: number; note: string | null } | null;
  /** Whether the cost is written out. Under it, the amount still turns red either way. */
  readonly showCost: boolean;
  readonly onClose: () => void;
  /** The amount, how many it was for, what they were, and what they looked like. */
  readonly onDone: (cents: number, count: number, said: string, photos: readonly Taken[]) => void;
}): React.ReactElement {
  const [cents, setCents] = useState(0);
  const [cards, setCards] = useState("");
  const [said, setSaid] = useState("");
  const [photos, setPhotos] = useState<Taken[]>([]);
  const [problem, setProblem] = useState<string | undefined>();
  const camera = useRef<HTMLInputElement>(null);
  /** Which number the keypad is filling in. The amount first, because it always matters. */
  const [field, setField] = useState<"amount" | "cards">("amount");

  async function add(files: FileList | readonly File[] | null): Promise<void> {
    if (!files) return;
    setProblem(undefined);
    for (const file of [...files].slice(0, 4)) {
      try {
        const shrunk = await shrink(file);
        setPhotos((current) => [...current, shrunk]);
      } catch {
        setProblem("That photo could not be read. Take it again");
      }
    }
    if (camera.current) camera.current.value = "";
  }

  usePastedImages((files) => void add(files));

  const digit = (key: string): void => {
    if (field === "amount") {
      setCents((was) => (was >= 1_000_000 ? was : Number(`${was}${key}`)));
    } else {
      setCards((was) => (was.length >= 4 ? was : `${was}${key}`.replace(/^0+(?=\d)/, "")));
    }
  };
  const back = (): void => {
    if (field === "amount") setCents((was) => Math.floor(was / 10));
    else setCards((was) => was.slice(0, -1));
  };

  return (
    <Modal
      open
      size="lg"
      title={name}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ti-btn ti-btn-light !py-3 !text-[1rem]" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="ti-btn ti-btn-primary-full !py-3 !text-[1rem]"
            disabled={cents <= 0}
            onClick={() => onDone(cents, Math.max(1, Number(cards) || 1), said.trim(), photos)}
          >
            Add to basket{cents > 0 ? ` - S$${formatMoney(cents)}` : ""}
          </button>
        </>
      }
    >
      <div className="grid md:grid-cols-2 gap-4 items-start">
        <div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <button
              type="button"
              className={`rounded-md border p-3 text-center ${
                showCost && floor && cents > 0 && cents < floor.cents
                  ? "border-danger bg-danger/10 text-danger"
                  : field === "amount"
                    ? "border-primary bg-primary/10"
                    : "border-defaultborder"
              }`}
              onClick={() => setField("amount")}
            >
              <div className="text-[0.75rem] text-[#8c9097] dark:text-white/50">Line total</div>
              <div className="font-semibold text-[2rem] leading-tight">{formatMoney(cents)}</div>
            </button>
            <button
              type="button"
              className={`rounded-md border p-3 text-center ${field === "cards" ? "border-primary bg-primary/10" : "border-defaultborder"}`}
              onClick={() => setField("cards")}
            >
              <div className="text-[0.75rem] text-[#8c9097] dark:text-white/50">Quantity</div>
              <div className="font-semibold text-[2rem] leading-tight">{cards || "1"}</div>
            </button>
          </div>

          {floor && showCost ? (
            <div
              className={`rounded-md border px-3 py-2 mb-3 text-[0.813rem] ${
                cents > 0 && cents < floor.cents
                  ? "border-danger/40 bg-danger/10 text-danger"
                  : "border-defaultborder bg-light dark:bg-bodybg text-[#8c9097] dark:text-white/50"
              }`}
            >
              <span className="font-semibold">
                {cents > 0 && cents < floor.cents ? "Below cost: " : "Cost "}
                S${formatMoney(floor.cents)} each
              </span>
              {floor.note ? <span className="block text-[0.75rem] mt-0.5">{workingOf(floor.note, floor.cents)}</span> : null}
            </div>
          ) : null}

          {problem ? <Alert tone="danger">{problem}</Alert> : null}

          <div className="mb-3">
            <label htmlFor="typed-said" className="form-label">
              Item description
            </label>
            <input
              id="typed-said"
              type="text"
              className="form-control"
              value={said}
              maxLength={120}
              placeholder="1 x Charizard EX and 1 x Marnie EX"
              onChange={(event) => setSaid(event.target.value)}
            />
            <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-1 mb-0">
              It appears on the sale and the invoice. This field is optional.
            </p>
          </div>

          <div className="flex items-center gap-3 mb-4 md:mb-0 overflow-x-auto">
            <button
              type="button"
              aria-label="Photograph what is being sold"
              className="shrink-0 w-[4.5rem] h-[4.5rem] rounded-md border border-dashed border-defaultborder flex flex-col items-center justify-center text-[#8c9097]"
              onClick={() => camera.current?.click()}
            >
              <i className="ri-camera-line text-[1.25rem]" />
              <span className="text-[0.6875rem] mt-1">Add photo</span>
          <span className="text-[0.5625rem] leading-none">or paste</span>
            </button>
            <input
              ref={camera}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              className="hidden"
              onChange={(event) => void add(event.target.files)}
            />
            {photos.map((photo) => (
              <div key={photo.id} className="shrink-0 w-[4.5rem] h-[4.5rem] rounded-md overflow-hidden border border-defaultborder relative">
                <img src={photo.preview} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  aria-label="Take this photo off"
                  className="absolute top-0 end-0 bg-danger text-white text-[0.625rem] px-1 rounded-bl"
                  onClick={() => setPhotos((current) => current.filter((one) => one.id !== photo.id))}
                >
                  <i className="ri-close-line" />
                </button>
                <button
                  type="button"
                  aria-label="Turn this photo"
                  title="Turn the photo"
                  className="absolute bottom-0 start-0 bg-dark/70 text-white text-[0.75rem] w-6 h-6 rounded-tr flex items-center justify-center"
                  onClick={(event) => {
                    event.stopPropagation();
                    void turned(photo).then((done) => setPhotos((current) => current.map((one) => (one.id === photo.id ? done : one))));
                  }}
                >
                  <i className="ri-clockwise-line" />
                </button>
              </div>
            ))}
          </div>
        </div>
        <Keypad onDigit={digit} onBack={back} />
      </div>
    </Modal>
  );
}

/** A plain count: how many of a product. */
export function PosNumber({
  title,
  label,
  start,
  onClose,
  onDone,
}: {
  readonly title: string;
  readonly label: string;
  readonly start: number;
  readonly onClose: () => void;
  readonly onDone: (value: number) => void;
}): React.ReactElement {
  const [text, setText] = useState(String(start));
  /** The first key replaces what was there, so 3 typed over 1 gives 3, not 13. */
  const [fresh, setFresh] = useState(true);
  const value = Number(text) || 0;

  return (
    <Modal
      open
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ti-btn ti-btn-light !py-3 !text-[1rem]" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="ti-btn ti-btn-primary-full !py-3 !text-[1rem]"
            disabled={value <= 0}
            onClick={() => onDone(value)}
          >
            {label}: {value}
          </button>
        </>
      }
    >
      <div className="text-center mb-4">
        <div className="text-[0.813rem] text-[#8c9097] dark:text-white/50">{label}</div>
        <div className="font-semibold text-[2.5rem] leading-tight">{value}</div>
      </div>
      <Keypad
        onDigit={(key) => {
          setText((was) => {
            const next = fresh ? key : `${was}${key}`;
            return next.replace(/^0+(?=\d)/, "").slice(0, 4);
          });
          setFresh(false);
        }}
        onBack={() => {
          setText((was) => was.slice(0, -1));
          setFresh(false);
        }}
      />
    </Modal>
  );
}
